import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Button } from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { useNavigate } from "react-router-dom";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import { useDispatch } from "react-redux";
import { getPagesMetaData } from "../../../../Api/Api";
import CODE from "../../../../Static/Constants/StatusCodes";
import { SetSessionExpired } from "../../../../Actions";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { usePermissions } from "../../../../Context/PermissionContext";
import TableHeader from "../../../../Features/Table/GenericDraggableTable/Renderers/TableHeader";

const normalizeColumnsConfig = (columnsConfig) =>
  Object.fromEntries(
    Object.entries(columnsConfig).map(([k, cfg]) => [
      k,
      { ...cfg, dataIndex: cfg.dataIndex || cfg.key || k },
    ])
  );

// Refs are passed so the render closure always reads the latest values
const buildActionColumn = (baseEntityLabelRef, navigateRef) => ({
  key: "actions",
  dataIndex: "actions",
  headerLabel: "Action",
  title: (titleProps) => (
    <TableHeader titleProps={titleProps} config={{ title: "Action" }} />
  ),
  fixed: undefined,
  width: 80,
  sorter: false,
  uniqueKey: "actionsUniqueKey",
  render: (_, row) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <PrimaryTooltip placement="bottom" title={`Access Granted ${baseEntityLabelRef.current}`}>
        <Button
          data-testid="client-api-view-entities-btn"
          onClick={() =>
            navigateRef.current(`/org-settings/client-api-management/${row.id}`, {
              state: {
                apiKeyName: row.name,
                baseEntityPluralLabel: baseEntityLabelRef.current,
              },
            })
          }
          sx={{
            p: 0.75,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            minWidth: 0,
            "&:hover": {
              backgroundColor: "#FFFFFF",
              border: "1px solid #6D72F6",
              "& svg path": { fill: "#6D72F6" },
            },
          }}
        >
          <FormatListBulletedIcon sx={{ fontSize: 18, color: "#494949" }} />
        </Button>
      </PrimaryTooltip>
    </Box>
  ),
});

export const useClientApiColumns = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission, isRbacV2Enabled } = usePermissions();

  const baseEntityLabelRef = useRef("Entities");
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const [allColumns, setAllColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [defaultColumnKeys, setDefaultColumnKeys] = useState([]);

  const shouldShowColumn = useCallback(
    (config) => {
      const codes = config?.controlledByFeatureCodes;
      if (!codes?.length) return true;
      return !isRbacV2Enabled() || codes.every((code) => hasPermission(code, "view"));
    },
    [hasPermission, isRbacV2Enabled]
  );

  useEffect(() => {
    loadColumns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadColumns = async () => {
    const res = await getPagesMetaData("ClientApiManagement");
    if (res?.responseStatus?.code === CODE.SUCCESS) {
      const metaInfo = res.response?.response?.metaInfo;
      const tableConfig = metaInfo?.table?.clientApiManagementTable;
      if (tableConfig) {
        // Read base entity label directly from this page's meta — the only reliable source
        const pluralLabel = metaInfo?.globalConfig?.baseEntity?.pluralLabel || "Entities";
        baseEntityLabelRef.current = pluralLabel;

        const built = getColumnsConfig({
          tableColumns: normalizeColumnsConfig(tableConfig.columnsConfig || {}),
          tableColumnOrder: tableConfig.columnOrdering || [],
          shouldShowColumn,
        });
        const actionCol = buildActionColumn(baseEntityLabelRef, navigateRef);
        const withAction = [...built, actionCol];
        setAllColumns(withAction);
        setVisibleColumns(withAction);
        setDefaultColumnKeys(withAction.map((col) => col.dataIndex));
      }
    } else if (res?.responseStatus?.code === CODE.SESSION_EXPIRED) {
      dispatch(SetSessionExpired(true));
    }
  };

  const applySelectedColumns = (newSelectedKeys) => {
    const filtered = allColumns.filter((col) =>
      newSelectedKeys.includes(col.dataIndex)
    );
    setVisibleColumns(filtered);
  };

  const selectedColumnKeys = visibleColumns.map((col) => col.dataIndex);

  return {
    allColumns,
    visibleColumns,
    setVisibleColumns,
    selectedColumnKeys,
    defaultColumnKeys,
    applySelectedColumns,
  };
};
